/**
 * BAB Report filter state, persisted to localStorage.
 *
 * localStorage rather than the session store: the whole filter block is meant to outlive the
 * tab and the browser restart, so nobody has to re-pick their view every morning. Same store
 * and the same reasoning as the column widths in useResizableColumns.ts — per browser profile,
 * which is per user in practice on a personal machine, but it does not follow the user to
 * another device.
 *
 * Two consequences worth knowing:
 *   - `periodYear`/`periodMonth` are written but never really restored: the page overrides them
 *     with the newest frozen period on every load (see BabReportPage), because a remembered
 *     period must not hide a freshly published month.
 *   - `selectedBuCode` / `selectedBabCompanyCode` / `selectedSourceCompany` now survive for
 *     weeks. That is deliberate. The cost is that a filter set long ago is still active and easy
 *     to skim past, so the figure on screen may be a scope rather than the group — the filter
 *     bar is the only thing saying so.
 */
import { useState, useCallback, useEffect } from 'react';
import type { BabReportFilters } from '../types/babReport';

const STORAGE_KEY = 'bab-report-filters';

const DEFAULT_FILTERS: BabReportFilters = {
    periodYear: null,
    periodMonth: null,
    selectedBuCode: 'ALL',
    selectedBabCompanyCode: 'ALL',
    selectedSourceCompany: '',
    maxViewLevel: 1,
    amountScale: 'thousands',
    signMode: 'bab',
    viewMode: 'YTD',
    mtdMonths: 12,
};

function loadFilters(): BabReportFilters {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
    } catch {
        // ignore corrupt storage
    }
    return { ...DEFAULT_FILTERS };
}

export function useBabReport() {
    const [filters, setFilters] = useState<BabReportFilters>(loadFilters);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
        } catch {
            // ignore quota errors
        }
    }, [filters]);

    const patch = useCallback((update: Partial<BabReportFilters>) => {
        setFilters(prev => ({ ...prev, ...update }));
    }, []);

    return { filters, setFilters, patch };
}
